<template>
  <v-card v-if="processedInstructions" class="mb-4" variant="text">
    <v-card-item class="pl-0">
      <v-card-title> Instructions </v-card-title>
      <template #prepend>
        <v-icon size="25px">{{ 'mdi-information-variant-circle-outline' }}</v-icon>
      </template>
    </v-card-item>
    <div class="pa-0">
      <div
        v-if="!editedItem.instructions"
        class="text-grey-darken-1 instructions-text"
        v-html="processedInstructions"
      ></div>
      <v-tooltip v-else location="bottom" open-delay="100">
        <div class="instructions-container" v-html="processedInstructions"></div>
        <template #activator="{ props }">
          <div class="instructions-text" v-bind="props" v-html="processedInstructions"></div>
        </template>
      </v-tooltip>
    </div>
  </v-card>
</template>

<script>
import markdownIt from 'markdown-it';
import { markdownItFancyListPlugin } from 'markdown-it-fancy-lists';
import { mapGetters } from 'vuex';
import {
  checkIfContextKeysExist,
  removeContextKeyClass,
  removeClickableProperty,
  mergeFieldToLowerCase,
  decodeHtmlEntities
} from '@/lib/util';

import nunjucks from 'nunjucks';
const md = markdownIt({
  html: true
});

md.use(markdownItFancyListPlugin);

// NOTE: Turning off autoescaping can be dangerous
const instructionNunjucks = nunjucks.configure('./', { autoescape: false, throwOnUndefined: true });

export default {
  name: 'InstructionsCard',
  props: {
    editedItem: {
      type: Object,
      default: () => ({}),
      required: true
    }
  },
  computed: {
    ...mapGetters({
      contextItemValues: 'clientContext/values'
    }),

    processedInstructions() {
      const errorMessageNotExistedContextKey =
        '<span style="color: red;">An issue occurred while loading this content. please reach out to Customer Support for assistance.</span>';

      try {
        const instructions = this.editedItem.instructions.default;
        const context = this.contextItemValues;

        if (!instructions || !checkIfContextKeysExist(instructions)) {
          return instructions;
        }

        // convert instructions to lower case
        const renderedInstructions = md.render(mergeFieldToLowerCase(instructions));

        // general check if the instructions have any invalid conditions and context key(s)
        // eslint-disable-next-line no-unused-vars
        let validRenderedInstructions = instructionNunjucks
          .renderString(renderedInstructions, context)
          .trim();

        // need to decode validRenderedInstructions due too validRenderedInstructions formatting now
        // is &lt;span class=&quot;validContext&quot;&gt;test&lt;/span&gt;
        // need to convert to be as <span class=\"validContext\">test</span>
        validRenderedInstructions = decodeHtmlEntities(validRenderedInstructions);

        const cleanedInstructionsContent = removeClickableProperty(
          removeContextKeyClass(validRenderedInstructions)
        );

        return md.render(cleanedInstructionsContent);
      } catch (error) {
        if (!this.editedItem.instructions) {
          return md.render("There's no instruction provided for this key.");
        }
        console.error('Error processing instructions:', error);
        // if there are any invalid conditions or context key(s), return the error message
        return errorMessageNotExistedContextKey;
      }
    }
  }
};
</script>

<style scoped>
.v-card {
  display: inline-block;
}
.v-card-title {
  font-size: 16px;
}
.instructions-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  -webkit-line-clamp: 3;
  text-overflow: ellipsis;
  white-space: normal;
  font-size: 0.875rem;
}

.instructions-container {
  max-width: 60vw;
}

.instructions-container ::v-deep(p),
.instructions-text ::v-deep(p) {
  font-size: 0.875rem;
  margin-bottom: 0 !important;
}
</style>
